import IamApi from "api/IamApi";
import { useBiller, usePaymentHistory } from "hooks";
import _ from "lodash";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import routing from "routing";
import { getDomain, mapSessionLanguageToUiLanguage } from "utilities/helper";
import { useTranslation } from "react-i18next";
import { getDeviceFingerprint } from "selectors/appState";
import { setPayableSession } from "actions/payableSessionActions";
import { MAX_RECENT_PAYMENTS } from "../common/payable-recent-payments";
import { ACTION } from "actions/paymentIntentActions";

export const PayablePayment = () => {
  const { i18n } = useTranslation();

  const deviceFingerprint = useSelector(getDeviceFingerprint);
  const dispatch = useDispatch();

  const { getBiller } = useBiller();
  const biller = getBiller();

  const location = useLocation();
  const navigate = useNavigate();

  const { loadRecentPayments, loadUpcomingPayment } = usePaymentHistory(true);

  useEffect(() => {
    const loadBillerAndPayments = async () => {
      const queryParams = new URLSearchParams(location.search);

      const spaceKrn = queryParams.get("spaceKrn");
      const payableKrn = queryParams.get("payableKrn");
      const hash = queryParams.get("hash");
      const language = queryParams.get("language");

      if (!(payableKrn && spaceKrn && hash && language)) {
        console.error(
          "Missing required information: payableKrn, spaceKrn, hash, language"
        );

        // TODO: may be display an error page instead of redirecting to the home page?
        navigate(routing.index());
        return;
      }

      const paymentRequest = {
        payableKrn,
        hash: hash,
      };

      try {
        const response = await IamApi.ezpay.getPayableLanding(
          deviceFingerprint,
          navigator.userAgent,
          getDomain(),
          spaceKrn,
          language,
          biller.clientId,
          paymentRequest
        );

        const payableSession = response.data;
        dispatch(setPayableSession(payableSession));

        const selectedLanguage = mapSessionLanguageToUiLanguage(
          language ?? "en"
        );
        i18n.changeLanguage(selectedLanguage);

        if (location.pathname === routing.payablePayment.index()) {
          // Load recent and upcoming payments, then navigate to recent payments.

          await Promise.all([
            loadRecentPayments(
              MAX_RECENT_PAYMENTS,
              biller.clientId,
              biller.realm
            ),
            loadUpcomingPayment(biller.clientId, biller.realm),
          ]);

          navigate(routing.payableRecentPayments.index());
        } else {
          // For direct landing, the API endpoint doesn't initiate a payment intent.
          // So we need to initiate payment intent here first before landing on
          // the payable dashboard page.
          await IamApi.ezpay.postPayableInitiateIntent(
            payableSession.payable.krn,
            deviceFingerprint,
            navigator.userAgent
          );

          const domain = getDomain();
          const newPayableSession = await IamApi.ezpay.getPayableFromSession(
            domain
          );

          dispatch(setPayableSession(newPayableSession));
          dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });

          navigate(routing.payableDashboard.index());
        }
      } catch (error) {
        console.log("Error while direct landing", error);
        navigate(routing.index());
      }
    };

    loadBillerAndPayments();

    // Only supposed to be run once, on initial mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};

export default PayablePayment;
